import { Badge, Box, Flex, HStack, Heading, Icon, Stack, Text, Textarea } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiFile } from "react-icons/fi";
import type { SelectionCriterionType, ViewDocumentAsOwnerDto } from "../../../../autogen/bff-api";
import { SELECTION_CRITERION_TYPE_OPTIONS } from "../../../sourcing-events/buyer/public/utils/publicProcurementOptions";
import { QualificationVerdictControl } from "./QualificationVerdictControl";

interface Props {
  type: SelectionCriterionType | null;
  name: string;
  documentationRequirement?: string | null;
  supplierDescription?: string | null;
  supplierEvidence?: ViewDocumentAsOwnerDto[];
  verdict: boolean | null;
  comment: string;
  onVerdictChange: (value: boolean | null) => void;
  onCommentChange: (value: string) => void;
  isReadOnly?: boolean;
}

const verdictBorderColor = (verdict: boolean | null) => {
  if (verdict === true) return "smSuccess";
  if (verdict === false) return "smError";
  return "smBorder";
};

export const QualificationReviewCard = ({
  type,
  name,
  documentationRequirement,
  supplierDescription,
  supplierEvidence = [],
  verdict,
  comment,
  onVerdictChange,
  onCommentChange,
  isReadOnly,
}: Props) => {
  const { t } = useTranslation();
  const typeOption = type ? SELECTION_CRITERION_TYPE_OPTIONS.find((o) => o.value === type) : undefined;

  return (
    <Box
      bg="smBackground"
      border="1px solid"
      borderColor={verdictBorderColor(verdict)}
      rounded="xl"
      px={{ base: "4", md: "6" }}
      py="5"
      transition="border-color 0.16s ease"
    >
      <Flex
        align={{ base: "flex-start", md: "flex-start" }}
        justify="space-between"
        gap="4"
        flexWrap="wrap"
        mb="3"
      >
        <Stack spacing="1.5" minW="0">
          {typeOption && (
            <Badge
              bg={typeOption.badgeBg}
              color={typeOption.badgeColor}
              textTransform="uppercase"
              px="2"
              py="0.5"
              borderRadius="md"
              w="fit-content"
            >
              {t(typeOption.value)}
            </Badge>
          )}
          <Heading as="h3" size="sm" color="smText">
            {name}
          </Heading>
          {documentationRequirement && (
            <Text fontSize="sm" color="smMuted">
              {t("Documentation requirement")}: {documentationRequirement}
            </Text>
          )}
        </Stack>
        <QualificationVerdictControl
          value={verdict}
          onChange={onVerdictChange}
          isReadOnly={isReadOnly}
        />
      </Flex>

      <Stack spacing="3">
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="smMuted"
          textTransform="uppercase"
          letterSpacing="wide"
        >
          {t("Supplier's answer")}
        </Text>
        {supplierDescription ? (
          <Box
            bg="smBackgroundSecondary"
            border="1px solid"
            borderColor="smBorder"
            rounded="lg"
            px="3"
            py="2.5"
            fontSize="sm"
            whiteSpace="pre-wrap"
          >
            {supplierDescription}
          </Box>
        ) : (
          <Box
            border="1px dashed"
            borderColor="smBorder"
            rounded="lg"
            px="3"
            py="2.5"
            fontSize="sm"
            fontStyle="italic"
            color="smMuted"
          >
            {t("Not answered by supplier")}
          </Box>
        )}
        {supplierEvidence.length > 0 && (
          <Stack spacing="1.5">
            {supplierEvidence.map((doc) => (
              <HStack
                key={doc.id}
                bg="smBackground"
                border="1px solid"
                borderColor="smBorder"
                rounded="md"
                px="3"
                py="1.5"
                spacing="2"
              >
                <Icon as={FiFile} color="smMuted" />
                <Text fontSize="sm">{doc.name}</Text>
              </HStack>
            ))}
          </Stack>
        )}

        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="smMuted"
          textTransform="uppercase"
          letterSpacing="wide"
          mt="1"
        >
          {t("Evaluation comment")}
        </Text>
        <Textarea
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
          placeholder={String(t("Write comment..."))}
          rows={3}
          isReadOnly={isReadOnly}
          resize="vertical"
        />
      </Stack>
    </Box>
  );
};
